/**
 * Shared per-item metadata on annotationUI.config.itemMetadata.
 * Legacy slide-level-classification slideInfo is migrated on read.
 */

import { normalizeFilename } from './itemInfoUtils';

function toTrimmedString(v) {
    return typeof v === 'string' ? v.trim() : String(v ?? '').trim();
}

/**
 * @param {object} [config] - annotationUI.config
 * @returns {object}
 */
export function getRawItemMetadataSection(config) {
    const raw = config && typeof config === 'object' ? config : {};
    if (raw.itemMetadata && typeof raw.itemMetadata === 'object') {
        return raw.itemMetadata;
    }
    if (raw.slideInfo && typeof raw.slideInfo === 'object') {
        return raw.slideInfo;
    }
    return {};
}

/**
 * @param {unknown} row
 */
export function normalizeItemMetadataRow(row) {
    if (!row || typeof row !== 'object') return { filename: '' };
    const out = {};
    Object.keys(row).forEach((k) => {
        const v = row[k];
        out[k] = v == null ? '' : String(v);
    });
    if (!Object.prototype.hasOwnProperty.call(out, 'filename')) {
        out.filename = '';
    }
    return out;
}

/**
 * @param {unknown} c
 * @returns {{ id: string, label: string, showInViewer: boolean } | null}
 */
export function normalizeItemMetadataColumnEntry(c) {
    if (!c || typeof c !== 'object') return null;
    const id = toTrimmedString(c.id);
    if (!id || id === 'filename') return null;
    return {
        id,
        label: c.label == null ? '' : String(c.label),
        showInViewer: c.showInViewer !== false,
    };
}

/** @deprecated */
export const normalizeSlideMetadataColumnEntry = normalizeItemMetadataColumnEntry;

function getMetadataColumnsFromSection(im) {
    const cols = im.itemMetadataColumns ?? im.slideMetadataColumns;
    return Array.isArray(cols) ? cols : [];
}

/**
 * @param {object} im - itemMetadata section (partial ok)
 * @param {object} rawConfig - full annotation UI config
 */
function migrateItemMetadataColumns(im, rawConfig) {
    const rows = Array.isArray(im.rows) ? im.rows.map((r) => normalizeItemMetadataRow(r)) : [];
    const smcRaw = getMetadataColumnsFromSection(im);
    if (smcRaw.length > 0) {
        const out = [];
        smcRaw.forEach((c) => {
            const n = normalizeItemMetadataColumnEntry(c);
            if (n) out.push(n);
        });
        if (out.length > 0) return out;
    }

    const displayRaw = Array.isArray(rawConfig.itemMetadataDisplayKeys)
        ? rawConfig.itemMetadataDisplayKeys
        : (Array.isArray(rawConfig.slideInfoDisplayKeys) ? rawConfig.slideInfoDisplayKeys : []);
    const displayKeys = displayRaw.map((k) => toTrimmedString(String(k))).filter(Boolean);
    const displaySet = new Set(displayKeys);
    const useSubset = displayKeys.length > 0;

    const clRaw = rawConfig.columnLabels && typeof rawConfig.columnLabels === 'object'
        ? rawConfig.columnLabels
        : {};

    const keySet = new Set();
    rows.forEach((r) => {
        Object.keys(r).forEach((k) => {
            if (k !== 'filename') keySet.add(k);
        });
    });
    displayKeys.forEach((k) => {
        if (k !== 'filename') keySet.add(k);
    });

    const sortedKeys = [...keySet].sort();
    return sortedKeys.map((id) => ({
        id,
        label: String(clRaw[id] == null ? '' : clRaw[id]),
        showInViewer: useSubset ? displaySet.has(id) : true,
    }));
}

/**
 * @param {{ id: string, label: string, showInViewer: boolean }[]} columns
 * @param {Array<Record<string, string>>} rows
 * @param {Record<string, string>} columnLabels
 */
export function mergeItemMetadataColumnsWithRowKeys(columns, rows, columnLabels) {
    const ids = new Set(columns.map((c) => c.id));
    const fromRows = new Set();
    (rows || []).forEach((r) => {
        if (!r || typeof r !== 'object') return;
        Object.keys(r).forEach((k) => {
            if (k !== 'filename') fromRows.add(k);
        });
    });
    const missing = [...fromRows].filter((id) => !ids.has(id)).sort();
    const cl = columnLabels && typeof columnLabels === 'object' ? columnLabels : {};
    const merged = columns.map((c) => ({ ...c }));
    missing.forEach((id) => {
        merged.push({
            id,
            label: String(cl[id] == null ? '' : cl[id]),
            showInViewer: true,
        });
    });
    return merged;
}

/** @deprecated */
export const mergeSlideMetadataColumnsWithRowKeys = mergeItemMetadataColumnsWithRowKeys;

/**
 * Must match girder_projects/slide_metadata_redaction.py visible_item_metadata_field_ids.
 *
 * @param {object} config - annotation UI config (raw or normalized)
 * @returns {string[]}
 */
export function getVisibleItemMetadataFieldIds(config) {
    const raw = config && typeof config === 'object' ? config : {};
    const im = getRawItemMetadataSection(raw);
    const rows = Array.isArray(im.rows) ? im.rows : [];

    const cols = getMetadataColumnsFromSection(im);
    if (cols.length > 0) {
        const out = [];
        cols.forEach((c) => {
            if (!c || typeof c !== 'object') return;
            if (c.showInViewer === false) return;
            const id = toTrimmedString(c.id);
            if (id && id !== 'filename') out.push(id);
        });
        return out;
    }

    const displayRaw = Array.isArray(raw.itemMetadataDisplayKeys)
        ? raw.itemMetadataDisplayKeys
        : (Array.isArray(raw.slideInfoDisplayKeys) ? raw.slideInfoDisplayKeys : []);
    const displayKeys = displayRaw.map((k) => toTrimmedString(String(k))).filter(Boolean);
    if (displayKeys.length > 0) {
        return displayKeys.filter((k) => k !== 'filename');
    }

    const keys = new Set();
    rows.forEach((r) => {
        if (!r || typeof r !== 'object') return;
        Object.keys(r).forEach((k) => {
            if (k !== 'filename') keys.add(k);
        });
    });
    return [...keys].sort();
}

/** @deprecated */
export const getVisibleSlideMetadataFieldIds = getVisibleItemMetadataFieldIds;

/**
 * @param {object} normalizedConfig - includes itemMetadata
 * @param {Record<string, string>|null} row
 * @returns {string[]}
 */
export function getViewerItemMetadataColumnOrder(normalizedConfig, row) {
    if (!row || typeof row !== 'object') return [];
    const visible = new Set(getVisibleItemMetadataFieldIds(normalizedConfig));
    const im = normalizedConfig.itemMetadata ?? {};
    const cols = getMetadataColumnsFromSection(im);
    const ordered = [];
    if (cols.length > 0) {
        cols.forEach((c) => {
            if (!c || c.showInViewer === false) return;
            const id = toTrimmedString(c.id);
            if (id && id !== 'filename' && visible.has(id) && id in row) {
                ordered.push(id);
            }
        });
    }
    const rest = Object.keys(row)
        .filter((k) => k !== 'filename' && visible.has(k) && !ordered.includes(k))
        .sort();
    return [...ordered, ...rest];
}

/** @deprecated */
export const getViewerSlideMetadataColumnOrder = getViewerItemMetadataColumnOrder;

/**
 * @param {object} normalizedConfig
 * @param {string} key
 * @returns {string}
 */
export function getViewerItemMetadataLabel(normalizedConfig, key) {
    const im = normalizedConfig.itemMetadata ?? {};
    const cols = getMetadataColumnsFromSection(im);
    if (Array.isArray(cols)) {
        const found = cols.find((c) => c && toTrimmedString(c.id) === key);
        if (found && toTrimmedString(found.label)) return toTrimmedString(found.label);
    }
    const cl = normalizedConfig.columnLabels && typeof normalizedConfig.columnLabels === 'object'
        ? normalizedConfig.columnLabels
        : {};
    const fromCl = toTrimmedString(cl[key] == null ? '' : cl[key]);
    return fromCl || key;
}

/** @deprecated */
export const getViewerSlideMetadataLabel = getViewerItemMetadataLabel;

/**
 * Visible metadata key/label/value tuples with non-empty values for viewer display.
 *
 * @param {object} uiConfig - annotationUI.config (raw or normalized)
 * @param {Record<string, string>|null|undefined} row
 * @returns {{ key: string, label: string, value: string }[]}
 */
export function getDisplayableItemMetadataEntries(uiConfig, row) {
    if (!row || typeof row !== 'object') return [];
    const normalized = normalizeItemMetadataConfig(uiConfig);
    const keys = getViewerItemMetadataColumnOrder(normalized, row);
    return keys
        .map((key) => ({
            key,
            label: getViewerItemMetadataLabel(normalized, key),
            value: String(row[key] ?? '').trim(),
        }))
        .filter((e) => e.value !== '');
}

const METADATA_SUMMARY_SEPARATOR = ' · ';

/**
 * Single-line summary for the top-bar metadata capsule.
 *
 * @param {{ key: string, label: string, value: string }[]} entries
 * @returns {string}
 */
export function formatItemMetadataSummary(entries) {
    if (!Array.isArray(entries) || entries.length === 0) return '';
    return entries
        .map(({ label, value }) => `${label}: ${value}`)
        .join(METADATA_SUMMARY_SEPARATOR);
}

/**
 * @param {object} config - annotationUI.config
 * @returns {object}
 */
export function trimItemMetadataLabelsForPersist(config) {
    if (!config || typeof config !== 'object') return config;
    let out;
    try {
        out = structuredClone(config);
    } catch {
        try {
            out = JSON.parse(JSON.stringify(config));
        } catch {
            return config;
        }
    }
    const im = out.itemMetadata ?? out.slideInfo;
    if (!im || typeof im !== 'object') return out;
    const cols = getMetadataColumnsFromSection(im);
    if (!Array.isArray(cols) || cols.length === 0) return out;

    const cl = {
        ...(out.columnLabels && typeof out.columnLabels === 'object' ? out.columnLabels : {}),
    };
    const newCols = cols.map((c) => {
        if (!c || typeof c !== 'object') return c;
        const id = toTrimmedString(c.id);
        if (!id) return c;
        const trimmedLabel = toTrimmedString(c.label == null ? '' : String(c.label));
        if (trimmedLabel) {
            cl[id] = trimmedLabel;
        } else {
            delete cl[id];
        }
        return {
            ...c,
            id,
            label: trimmedLabel,
        };
    });
    const nextIm = {
        ...im,
        itemMetadataColumns: newCols,
    };
    delete nextIm.slideMetadataColumns;
    out.itemMetadata = nextIm;
    delete out.slideInfo;
    out.columnLabels = cl;
    const displayKeys = newCols
        .filter((c) => c && c.showInViewer !== false && toTrimmedString(c.id))
        .map((c) => toTrimmedString(c.id));
    out.itemMetadataDisplayKeys = displayKeys;
    delete out.slideInfoDisplayKeys;
    return out;
}

/** @deprecated */
export const trimSlideMetadataLabelsForPersist = trimItemMetadataLabelsForPersist;

export const DEFAULT_ITEM_METADATA = {
    rows: [],
    trimWhitespace: true,
    caseInsensitive: false,
    itemMetadataColumns: [],
};

/**
 * Normalize itemMetadata slice and related top-level keys.
 * @param {object} [config] - annotationUI.config
 */
export function normalizeItemMetadataConfig(config) {
    const raw = config && typeof config === 'object' ? config : {};
    const imRaw = getRawItemMetadataSection(raw);
    const rowsRaw = Array.isArray(imRaw.rows) ? imRaw.rows : [];
    const rows = rowsRaw.map((r) => normalizeItemMetadataRow(r));

    const trimWhitespace = imRaw.trimWhitespace !== false;
    const caseInsensitive = Boolean(imRaw.caseInsensitive);

    const itemMetadataColumns = migrateItemMetadataColumns(
        { ...imRaw, rows },
        raw,
    );

    const itemMetadataDisplayKeys = itemMetadataColumns.length > 0
        ? itemMetadataColumns.filter((c) => c.showInViewer).map((c) => c.id)
        : (Array.isArray(raw.itemMetadataDisplayKeys)
            ? raw.itemMetadataDisplayKeys
            : (Array.isArray(raw.slideInfoDisplayKeys) ? raw.slideInfoDisplayKeys : []))
            .map((k) => toTrimmedString(String(k)))
            .filter(Boolean);

    const clRaw = raw.columnLabels && typeof raw.columnLabels === 'object' ? raw.columnLabels : {};
    const columnLabels = {};
    Object.keys(clRaw).forEach((k) => {
        const key = toTrimmedString(String(k));
        if (!key) return;
        const v = clRaw[k];
        columnLabels[key] = v == null ? '' : String(v);
    });

    return {
        itemMetadata: {
            rows,
            trimWhitespace,
            caseInsensitive,
            itemMetadataColumns,
        },
        itemMetadataDisplayKeys,
        columnLabels,
    };
}

/**
 * Strip legacy slideInfo keys; persist canonical itemMetadata only.
 * @param {object} config
 */
export function stripLegacyItemMetadataKeys(config) {
    if (!config || typeof config !== 'object') return config;
    const { slideInfo: _si, slideInfoDisplayKeys: _sdk, ...rest } = config;
    return rest;
}

/**
 * @param {{ id?: string, config?: object }} annotationUI
 */
export function prepareItemMetadataForAnnotationUISave(annotationUI) {
    const ann = annotationUI && typeof annotationUI === 'object' ? annotationUI : {};
    const cfg = ann.config && typeof ann.config === 'object' ? ann.config : {};
    const trimmed = trimItemMetadataLabelsForPersist(cfg);
    const normalized = normalizeItemMetadataConfig(trimmed);
    const merged = {
        ...stripLegacyItemMetadataKeys(trimmed),
        ...normalized,
    };
    return {
        ...ann,
        config: merged,
    };
}

/**
 * @param {object} config - should be normalized
 * @returns {{ code: string, message: string }[]}
 */
export function validateItemMetadataConfig(config) {
    const c = normalizeItemMetadataConfig(config);
    const issues = [];
    const im = c.itemMetadata ?? {};
    const rows = Array.isArray(im.rows) ? im.rows : [];
    const matchOpts = {
        trimWhitespace: im.trimWhitespace !== false,
        caseInsensitive: Boolean(im.caseInsensitive),
    };

    const seenFileKeys = new Map();
    rows.forEach((row, i) => {
        const rowNum = i + 1;
        const fn = row?.filename ?? '';
        const key = normalizeFilename(fn, matchOpts);
        if (!key) {
            issues.push({
                code: 'empty_item_filename',
                message: `Item metadata row ${rowNum}: filename is empty.`,
            });
            return;
        }
        if (seenFileKeys.has(key)) {
            const first = seenFileKeys.get(key);
            const display = String(fn).trim() || fn;
            issues.push({
                code: 'duplicate_item_filename',
                message: `Item metadata row ${rowNum}: duplicate filename match for "${display}" (same as row ${first}).`,
            });
        } else {
            seenFileKeys.set(key, rowNum);
        }
    });

    getMetadataColumnsFromSection(im).forEach((col, i) => {
        const id = toTrimmedString(col?.id);
        if (id === 'filename') {
            issues.push({
                code: 'reserved_item_column_id',
                message: `Item metadata column ${i + 1}: id "filename" is reserved; use a different key.`,
            });
        }
    });

    return issues;
}

/**
 * @param {{ id?: string, config?: object }} annotationUI
 * @returns {string[]}
 */
export function getItemMetadataConfigPublishWarnings(annotationUI) {
    const config = annotationUI?.config;
    if (!config || typeof config !== 'object') return [];
    return validateItemMetadataConfig(config).map((i) => i.message);
}
